/**
 * Client-portal API client. Talks to the `api/portal/*` surface as an
 * authenticated CUSTOMER (a Contact), a plane entirely separate from the staff
 * session. It attaches the portal session token as a Bearer and nothing else —
 * never the staff `x-tenant-id`/`x-api-key`, so a logged-in operator opening a
 * portal link can't bleed their session into it. The tenant is resolved from
 * the `:slug` in the URL path, server-side.
 */
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { normalizeError } from '../api/errors';
import { TIMEOUT_MS } from '../api/client';

let getSessionToken: () => string | null = () => null;

/** Wire the token getter to the portal auth store (called once at app init). */
export function registerPortalTokenSource(getter: () => string | null): void {
  getSessionToken = getter;
}

function attachPortalSession(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  // Strip any staff headers that global defaults might inject.
  config.headers.delete('x-tenant-id');
  config.headers.delete('x-api-key');
  const token = getSessionToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers.delete('Authorization');
  }
  return config;
}

export const customerClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_OPERATIONS_URL ?? '',
  timeout: TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

customerClient.interceptors.request.use(attachPortalSession, (error) =>
  Promise.reject(normalizeError(error)),
);
customerClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeError(error)),
);

export async function portalGet<T>(url: string): Promise<T> {
  const { data } = await customerClient.get<T>(url);
  return data;
}

export async function portalPost<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await customerClient.post<T>(url, body ?? {});
  return data;
}

export async function portalDelete<T>(url: string): Promise<T> {
  const { data } = await customerClient.delete<T>(url);
  return data;
}
